import Vue from 'vue'
import filterProfaneWords from 'storybook-utils/profanity-filter/filterProfaneWords'
import Constant from '@/constants'
import EventBus from '@/eventBus'

const WORD_CLOUD_TRANSITION_DURATION = 600
const DEFAULT_HEIGHT = 480
const DEFAULT_WIDTH = 1180

export default (WordCloud) => Vue.component('WordCloudContainer', {
  props: {
    options: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
      myWordCloud: null,
      isLoading: false,
      filteringProfanity: false,
    }
  },
  mounted() {
    this.calculateAndSetScale()
    this.renderWordCloud()
    EventBus.$on('draw-wordcloud', () => {
      this.myWordCloud.update(this.getWords())
    })
    window.addEventListener('resize', this.calculateAndSetScale)
  },
  destroyed() {
    EventBus.$off('draw-wordcloud')
    window.removeEventListener('resize', this.calculateAndSetScale)
  },
  watch: {
    textColour: function watchTextColour() {
      this.renderWordCloud()
    },
    filteringProfanity: function watchFilteringProfanity() {
      this.renderWordCloud()
    },
  },
  methods: {
    calculateAndSetScale() {
      const { innerWidth } = window
      const scale = (innerWidth / DEFAULT_WIDTH)
      // set style here instead of using computed property in WordCloud.vue
      // so that all logic is in one place
      const wordCloud = document.getElementById('word-cloud')
      wordCloud.style.transform = `scale(${scale})`

      const wordCloudContainer = document.querySelector('.wordcloud-container')
      wordCloudContainer.style.height = `${scale * DEFAULT_HEIGHT}px`
      wordCloudContainer.style.width = `${innerWidth}px` // set correct width for capturing image when click Download
    },
    getWords() {
      // copied and modified from presenter's WordCloudContent.vue
      let arr = []
      this.options.forEach((item) => {
        if (item.title) {
          item.title = item.title.trim()
          item.title = item.title.toLowerCase()
          if (item.title.length > 25) {
            item.title = item.title.slice(0, 25)
          }

          const index = arr.findIndex((option) => {
            return option.originalText == item.title
          })

          if (index === -1) {
            const word = {
              text: this.filteringProfanity && item.title
                ? filterProfaneWords(item.title)
                : item.title,
              originalText: item.title,
              count: 1,
              length: item.title.length,
            }
            arr.push(word)
          } else {
            arr[index].count++
          }
        }
      })
      arr = arr.sort((a, b) => {
        return b.count - a.count
      })

      if (arr.length) {
        let total = 0
        const pool = 30000
        arr.forEach((item) => {
          total += item.count
        })

        arr.forEach((item, key) => {
          item.percent = (item.count / total) * 100

          item.size = Math.sqrt((pool / 100) * item.percent)

          if (item.size > 90) {
            item.size = 90
          } else if (item.size < 16) {
            item.size = 16
          }
          let indexColor = key
          if (key > 29) {
            indexColor = key - parseInt(key / 29, 10) * 29
          }
          item.color = Constant.colors[indexColor]
        })

        if (arr.length) {
          arr[0].isMax = true
        }
      }
      return arr
    },
    renderWordCloud() {
      // copied and modified from presenter's WordCloudContent.vue
      const self = this
      const { d3 } = window
      if (document.getElementById('word-cloud')) {
        document.getElementById('word-cloud').innerHTML = ''
      }

      // Simple animated example of d3-cloud - https://github.com/jasondavies/d3-cloud
      // Based on https://github.com/jasondavies/d3-cloud/blob/master/examples/simple.html

      // Encapsulate the word cloud functionality

      // Encapsulate the word cloud functionality
      function wordCloud(selector) {
        // Construct the word cloud's SVG element
        const svg = d3
          .select(selector)
          .append('svg')
          .attr('width', DEFAULT_WIDTH)
          .attr('height', DEFAULT_HEIGHT)
          .append('g')
          .attr('transform', `translate(${DEFAULT_WIDTH/2},${DEFAULT_HEIGHT/2})`)

        // Draw the word cloud
        function draw(words) {
          const cloud = svg.selectAll('g text').data(words, (d) => {
            return d.originalText
          })

          // Entering words
          cloud
            .enter()
            .append('text')
            .style('font-family', "'Nunito', sans-serif")
            .style('font-weight', 'bold')
            .style('fill', (d) => {
              return d.color
            })
            .attr('text-anchor', 'middle')
            .attr('font-size', 1)
            .text((d) => {
              return d.text
            })
            .on('click', (d) => {
              const textIndex = words.findIndex((item) => {
                return item.originalText === d.originalText
              })
              const textRemove = words[textIndex].originalText

              self.options.forEach((item) => {
                if (item.title) {
                  item.title = item.title.trim()
                  item.title = item.title.toLowerCase()
                  if (item.title.length > 25) {
                    item.title = item.title.slice(0, 25)
                  }
                }
              })
              const arr = []
              self.options.forEach((data) => {
                if (data.title != textRemove) {
                  arr.push(data)
                }
              })
              EventBus.$emit('update-words', arr)
            })

          // Entering and existing words
          cloud
            .transition()
            .duration(WORD_CLOUD_TRANSITION_DURATION)
            .style('font-size', (d) => {
              return `${d.size}px`
            })

            .attr('transform', (d) => {
              return `translate(${[d.x, d.y]})rotate(${d.rotate})`
            })
            .style('fill-opacity', 1)

          // Exiting words
          cloud
            .exit()
            .transition()
            .duration(200)
            .style('fill-opacity', 1e-6)
            .attr('font-size', 1)
            .remove()
        }

        // Use the module pattern to encapsulate the visualisation code. We'll
        // expose only the parts that need to be public.
        return {
          // Recompute the word cloud for a new set of words. This method will
          // asycnhronously call draw when the layout has been computed.
          // The outside world will need to call this function, so make it part
          // of the wordCloud return value.
          update(words) {
            d3.layout
              .cloud()
              .size([DEFAULT_WIDTH, DEFAULT_HEIGHT])
              .words(words)
              .padding(10)
              .rotate((d) => {
                if (d.isMax || d.text.length > 18 || d.size > 50) {
                  return 0
                }

                return ~~(Math.random() * 2) * 270
              })
              .font("'Nunito', sans-serif")
              .fontSize((d) => {
                return d.size
              })
              .on('end', draw)
              .start()
          },
        }
      }

      // Some sample data - http://en.wikiquote.org/wiki/Opening_lines

      // Prepare one of the sample sentences by removing punctuation,
      // creating an array of words and computing a random size attribute.

      // This method tells the word cloud to redraw with a new set of words.
      // In reality the new words would probably come from a server request,
      // user input or some other source.
      function showNewWords(vis) {
        vis.update(self.getWords())
      }

      // Create a new instance of the word cloud visualisation.
      self.myWordCloud = wordCloud('#word-cloud')

      // Start cycling through the demo data
      showNewWords(self.myWordCloud)
    },
  },

  render(createElement) {
    return createElement(WordCloud, {
      props: {
        ...this.$attrs,
      },
    })
  },
})

